import { ArrowSquareOut } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useState } from "react";
import { toast } from "react-toastify";

import TimeframeSelector from "common/components/TimeframeSelector";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FilterButton from "common/components/ui/FilterButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectTrigger,
  SelectValue,
} from "common/components/ui/Select";
import WorkspaceAccountSelector from "common/components/WorkspaceAccountSelector";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useSelectedTimeframe from "common/hooks/useSelectedTimeframe";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { MetricsParameters } from "common/types";

import useWorkspaceReport from "../../datahooks/useWorkspaceReport";
import ReportReady from "./ReportReady";

type ExportFormat = "pdf" | "csvCampaigns" | "csvInteractions";
function ReportItem({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <div className="flex border-b gap-4 border-b-black-200 py-5 first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex flex-1 flex-col gap-y-0.5">
        <span className="text-md-bold lg:text-button-md">{title}</span>
        <span className="text-xs text-black-500">{subtitle}</span>
      </div>

      {children}
    </div>
  );
}
export default function ExportReport({
  initialTimeframe,
}: {
  initialTimeframe: string | MetricsParameters;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const workspace = useSelectedWorkspaceContext();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [exportFormat, setExportFormat] = useState<ExportFormat>("pdf");
  const { setSelectedTimeframe, selectedTimeframe, selectedTimeframeOption } =
    useSelectedTimeframe(initialTimeframe);
  const [exportUrl, setExportUrl] = useState<string>();
  const { getReportToken, isGettingReportToken, getReport, isGettingReport } =
    useWorkspaceReport();

  useEffect(() => {
    setSelectedTimeframe(initialTimeframe);
  }, [initialTimeframe]);

  function onNext() {
    setIsOpen(false);

    if (exportFormat !== "pdf") {
      toast("Your export is preparing, you will be notified when it's ready");
    }

    getReportToken({
      params: selectedTimeframeOption.getParameters(workspace),
      accountIds:
        selectedAccountIds.length === 0
          ? workspace.accounts.map(({ id }) => id)
          : selectedAccountIds,
    }).then((token) => {
      if (exportFormat === "pdf") {
        setExportUrl(`${import.meta.env.VITE_WHITE_LABEL_URL}report/#${token}`);
      } else {
        getReport({
          type:
            exportFormat === "csvInteractions" ? "interactions" : "campaigns",
          token,
        }).then((url) => {
          setExportUrl(url);
        });
      }
    });
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Title = isDesktop ? DialogTitle : DrawerTitle;

  return (
    <>
      <Component
        open={isOpen}
        onOpenChange={setIsOpen}
        onAfterClose={() => {
          setExportFormat("pdf");
          setSelectedAccountIds([]);
          setSelectedTimeframe(initialTimeframe);
        }}
      >
        <Trigger asChild>
          <Button
            size="md"
            rightIcon={<ArrowSquareOut />}
            variant="secondary-purple"
          >
            Export report
          </Button>
        </Trigger>
        <Content className="lg:w-[448px]">
          <Title className="mb-4 lg:mb-8">Generate your report</Title>
          <div className="mb-4 flex flex-col">
            <ReportItem
              title="Timeframe"
              subtitle="Select the report timeframe"
            >
              <TimeframeSelector
                selectedTimeframe={selectedTimeframe}
                selectTimeframe={setSelectedTimeframe}
                className="w-[172px] lg:w-48"
              />
            </ReportItem>
            <ReportItem
              title="LinkedIn accounts"
              subtitle="Accounts to include in the report"
            >
              <WorkspaceAccountSelector
                selection={selectedAccountIds}
                setSelection={setSelectedAccountIds}
                className="w-[172px] lg:w-48"
              />
            </ReportItem>
            <ReportItem
              title="Format"
              subtitle="Select the best format for your report "
            >
              <Select
                value={exportFormat}
                onValueChange={(value: ExportFormat) => setExportFormat(value)}
              >
                <SelectTrigger asChild>
                  <FilterButton className="w-[172px] lg:w-48">
                    <SelectValue placeholder="Select a workspace" />
                  </FilterButton>
                </SelectTrigger>

                <SelectContent className="z-aboveDialog" align="end">
                  <SelectItem value="pdf">
                    <SelectItemText>Web Page (PDF)</SelectItemText>
                  </SelectItem>
                  <SelectItem value="csvInteractions">
                    <SelectItemText>Interactions (CSV)</SelectItemText>
                  </SelectItem>
                  <SelectItem value="csvCampaigns">
                    <SelectItemText>Campaigns (CSV)</SelectItemText>
                  </SelectItem>
                </SelectContent>
              </Select>
            </ReportItem>
          </div>

          <div className="flex justify-between pt-4 lg:pt-5">
            <Close asChild>
              <Button
                variant="secondary-black"
                disabled={isGettingReportToken || isGettingReport}
              >
                Back
              </Button>
            </Close>
            <Button
              variant="default-black"
              isLoading={isGettingReportToken || isGettingReport}
              onClick={onNext}
            >
              Next
            </Button>
          </div>
        </Content>
      </Component>
      <ReportReady
        exportUrl={exportUrl}
        resetUrl={() => setExportUrl(undefined)}
        isPdf={exportFormat === "pdf"}
      />
    </>
  );
}
